import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { authApi, inviteApi } from '@/lib/api/auth';
import { useAuthStore } from '@/stores/authStore';

interface SetupFormProps {
  token: string;
}

interface InvitePreview {
  email: string;
  role: string;
  org_name?: string;
}

export function SetupForm({ token }: SetupFormProps) {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    inviteApi.previewSetup(token)
      .then(setPreview)
      .catch((err) => setPreviewError(err.response?.data?.error || 'Invalid or expired invite'));
  }, [token]);

  if (previewError) {
    return (
      <div className="mx-auto max-w-md rounded-xl border border-border bg-background p-8 text-center">
        <p className="font-medium">{previewError}</p>
        <p className="mt-2 text-sm text-muted-foreground">Ask your trainer or admin to send a fresh invite.</p>
      </div>
    );
  }
  if (!preview) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  const roleLabel: Record<string, string> = { org_admin: 'Org Admin', trainer: 'Trainer', student: 'Student' };

  const handlePasswordSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const data = await authApi.setup({ token, username, password });
      setAuth(data.access_token);
      toast.success('Account activated!');
      navigate({ to: '/dashboard' });
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Setup failed.');
    } finally { setSubmitting(false); }
  };

  const handleOAuthSetup = async (provider: 'google' | 'github') => {
    try {
      const { redirect_url } = await inviteApi.startSetupOAuth({ token, provider });
      window.location.href = redirect_url;
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to start OAuth flow');
    }
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-xl border border-border bg-background p-8 shadow-lg">
      <h1 className="text-2xl font-bold font-heading">Set up your account</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        You&apos;re invited to <strong>{preview.org_name || 'KodPrep'}</strong> as <strong>{roleLabel[preview.role] || preview.role}</strong>. Choose how you&apos;d like to sign in.
      </p>

      <div className="mt-6 flex flex-col gap-3">
        <button onClick={() => handleOAuthSetup('google')} className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted">
          Continue with Google
        </button>
      </div>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">OR set a password</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={handlePasswordSetup} className="flex flex-col gap-4">
        <input required placeholder="Choose a username" value={username} onChange={(e) => setUsername(e.target.value)}
          className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm" />
        <input required type="password" placeholder="Password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)}
          className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm" />
        <input required type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
          className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm" />
        <button type="submit" disabled={submitting} className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {submitting ? 'Activating…' : 'Activate account'}
        </button>
      </form>
    </div>
  );
}
